import { Clock } from 'lucide-react';
import { Button, Modal } from '@/components/ui';

interface SessionTimeoutWarningModalProps {
  readonly isOpen: boolean;
  readonly secondsRemaining: number;
  readonly onStayLoggedIn: () => void;
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Shown when the user has been idle and is about to be logged out.
 * Dismissing via the X button, pressing Escape, or clicking "Stay Logged In"
 * all count as user activity and reset the idle timer.
 */
export function SessionTimeoutWarningModal({
  isOpen,
  secondsRemaining,
  onStayLoggedIn,
}: SessionTimeoutWarningModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onStayLoggedIn} title="Session About to Expire">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 shrink-0 text-amber-500" size={20} aria-hidden="true" />
          <p className="text-sm text-cool-gray-11">
            Your session will expire due to inactivity in{' '}
            <span
              className="font-semibold text-dds-red"
              aria-live="polite"
              aria-atomic="true"
            >
              {formatCountdown(secondsRemaining)}
            </span>
            {'. '}Click <strong>Stay Logged In</strong> to continue your session.
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="button" variant="primary" onClick={onStayLoggedIn}>
            Stay Logged In
          </Button>
        </div>
      </div>
    </Modal>
  );
}
